import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from './fs';
import { db } from './firebase';
import { CLINICAS } from './clinicas';

// Respuestas rápidas del equipo. Se guardan en config/respuestas.
// Marcadores: {nombre} (cliente), {clinica}, {telefono}
export const RESPUESTAS_BASE = [
  { id: 'ayuno', titulo: 'Ayuno cirugía', texto: 'Buenos días{nombre}. Para la intervención de mañana, su mascota debe venir en ayunas: sin comida desde las 22:00 de la noche anterior. El agua puede dejársela hasta 2 horas antes de venir. Si toma alguna medicación, consúltenos antes de dársela. Un saludo, {clinica}.' },
  { id: 'postop', titulo: 'Cuidados tras cirugía', texto: 'Le recordamos los cuidados tras la cirugía: mantenga el collar isabelino puesto en todo momento, evite que se lama o muerda la herida, reposo sin saltos ni carreras durante 10-14 días y revise la herida a diario. Si nota sangrado, inflamación, mal olor o que no come, llámenos al {telefono}. Un saludo, {clinica}.' },
  { id: 'pastilla', titulo: 'Cómo dar pastilla', texto: 'Para darle la pastilla puede esconderla en un trocito de comida húmeda, jamón york o queso. Si no la acepta, abra suavemente la boca, colóquela al fondo de la lengua, cierre la boca y masajee el cuello hasta que trague. Después ofrézcale un premio o algo de agua. Si tiene cualquier duda, llámenos al {telefono}.' },
  { id: 'blanda', titulo: 'Dieta blanda', texto: 'Le indicamos la dieta blanda: arroz blanco bien cocido con pollo o pavo cocido, sin piel, sin huesos y sin sal, en varias tomas pequeñas al día durante 2-3 días. Después vaya mezclándolo poco a poco con su pienso habitual. Si sigue con vómitos o diarrea, o lo nota decaído, llámenos al {telefono}.' },
  { id: 'orina', titulo: 'Muestra de orina', texto: 'Para la muestra de orina: recójala por la mañana, a ser posible la primera del día, en un bote estéril (se compra en farmacia). Guárdela en la nevera y tráiganosla en menos de 4 horas. Un saludo, {clinica}.' },
  { id: 'heces', titulo: 'Muestra de heces', texto: 'Para el análisis de heces: recoja una muestra de unos 3 días seguidos (una pequeña cantidad de cada día) en un bote estéril de farmacia y guárdela en la nevera hasta traerla. Un saludo, {clinica}.' },
  { id: 'resultados', titulo: 'Resultados listos', texto: 'Buenos días{nombre}. Ya tenemos los resultados de su mascota. El veterinario le llamará para comentárselos o, si lo prefiere, puede llamarnos al {telefono}. Un saludo, {clinica}.' },
  { id: 'presupuesto', titulo: 'Presupuesto enviado', texto: 'Buenos días{nombre}. Le hemos preparado el presupuesto que nos pidió. Si tiene cualquier duda o quiere reservar fecha, puede responder a este mensaje o llamarnos al {telefono}. Un saludo, {clinica}.' },
  { id: 'recoger', titulo: 'Ya puede recogerlo', texto: '¡Buenas noticias{nombre}! Su mascota ya está despierta y bien. Puede pasar a recogerla cuando le venga bien dentro de nuestro horario. Un saludo, {clinica}.' },
];

export function useRespuestas() {
  const [lista, setLista] = useState(RESPUESTAS_BASE);
  useEffect(() => onSnapshot(doc(db, 'config', 'respuestas'),
    (s) => { const l = s.data()?.lista; if (Array.isArray(l) && l.length) setLista(l); }, () => {}), []);
  return lista;
}

// Saludo según la hora de Madrid: días hasta las 14, tardes hasta las 21, noches después
export const saludo = (d = new Date()) => {
  const h = Number(new Intl.DateTimeFormat('es-ES', { hour: 'numeric', hour12: false, timeZone: 'Europe/Madrid' }).format(d));
  return h >= 6 && h < 14 ? 'Buenos días' : h >= 14 && h < 21 ? 'Buenas tardes' : 'Buenas noches';
};

export const rellenar = (texto, { nombre, clinica } = {}) => {
  const c = CLINICAS.find((x) => x.id === clinica);
  const n = (nombre || '').trim().split(/\s+/)[0];
  return String(texto || '')
    .replaceAll('{saludo}', saludo())
    .replace(/Buen(os días|as tardes|as noches)/g, saludo())
    .replace(/\s*\{nombre\}/g, n ? `, ${n}` : '') // AUD-WA-06: sin nombre no queda «Hola , …»
    .replaceAll('{clinica}', c?.marca?.split(' / ')[0] || c?.nombre || 'la clínica')
    .replaceAll('{telefono}', c?.telefono || 'teléfono de la clínica'); // AUD-WA-08: «llamarnos al teléfono de la clínica»
};

// Botonera para el chat
export function BotonesRespuesta({ onElegir }) {
  const lista = useRespuestas();
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="resp-rapidas">
      <button className="btn sec mini" onClick={() => setAbierto(!abierto)}>⚡ Respuestas rápidas {abierto ? '▲' : '▼'}</button>
      {abierto && (
        <div className="resp-chips">
          {lista.map((r) => <button key={r.id} className="chip resp-chip" onClick={() => { onElegir(r.texto); setAbierto(false); }}>{r.titulo}</button>)}
        </div>
      )}
    </div>
  );
}

// Pestaña para ver, copiar y (admin) editar
export function Respuestas({ esAdmin, clinica }) {
  const lista = useRespuestas();
  const [edit, setEdit] = useState(null);
  const [copiado, setCopiado] = useState('');
  const [elegida, setElegida] = useState(CLINICAS[0].id);
  const fija = CLINICAS.some((c) => c.id === clinica);
  const cl = fija ? clinica : elegida;

  const guardar = (nueva) => setDoc(doc(db, 'config', 'respuestas'), { lista: nueva }, { merge: true }).catch((e) => alert(e.message));
  const copiar = async (r) => {
    const t = rellenar(r.texto, { clinica: cl });
    try { await navigator.clipboard.writeText(t); } catch { prompt('Copia el texto:', t); }
    setCopiado(r.id); setTimeout(() => setCopiado(''), 1500);
  };
  const guardarEdit = () => {
    if (!edit.titulo.trim() || !edit.texto.trim()) return;
    const limpio = { ...edit, titulo: edit.titulo.trim().slice(0, 60), texto: edit.texto.trim().slice(0, 1000) };
    const nueva = lista.some((r) => r.id === edit.id) ? lista.map((r) => (r.id === edit.id ? limpio : r)) : [...lista, limpio];
    guardar(nueva); setEdit(null);
  };

  return (
    <div>
      <p className="tenue" style={{ marginTop: 0 }}>
        En el chat pulsa <b>⚡ Respuestas rápidas</b> y se escribe sola (luego puedes retocarla). Aquí puedes copiarlas para pegarlas en WhatsApp del móvil.
        {esAdmin && ' Marcadores: {saludo}, {nombre}, {clinica}, {telefono}. "Buenos días" cambia solo a tardes (14 h) o noches (21 h).'}
      </p>
      {!fija && (
        <div className="filtros" style={{ marginBottom: '.75rem' }}>
          {CLINICAS.map((c) => <button key={c.id} className={cl === c.id ? 'on' : ''} onClick={() => setElegida(c.id)}>{c.nombre}</button>)}
        </div>
      )}
      {esAdmin && !edit && <button className="btn" style={{ marginBottom: '.75rem' }} onClick={() => setEdit({ id: 'r' + Date.now(), titulo: '', texto: '' })}>+ Nueva respuesta</button>}
      {edit && (
        <div className="tarjeta">
          <input placeholder="Título (ej.: Ayuno cirugía)" value={edit.titulo} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })} style={{ width: '100%', marginBottom: '.5rem' }} />
          <textarea rows={5} value={edit.texto} onChange={(e) => setEdit({ ...edit, texto: e.target.value })} style={{ width: '100%' }} placeholder="Texto, de usted" />
          <div className="botones" style={{ marginTop: '.5rem' }}>
            <button className="btn" disabled={!edit.titulo.trim() || !edit.texto.trim()} onClick={guardarEdit}>Guardar</button>
            {(!edit.titulo.trim() || !edit.texto.trim()) && <small className="tenue"> Pon un título y un texto para guardar.</small>}
            <button className="btn sec" onClick={() => setEdit(null)}>Cancelar</button>
          </div>
        </div>
      )}
      <div className="resp-lista">
        {lista.map((r) => (
          <div key={r.id} className="tarjeta resp-item">
            <div className="wa-fila"><b>{r.titulo}</b>
              <div className="botones">
                <button className="btn sec mini" onClick={() => copiar(r)}>{copiado === r.id ? '✓ Copiado' : 'Copiar'}</button>
                {esAdmin && <button className="btn sec mini" onClick={() => setEdit({ ...r })}>Editar</button>}
                {esAdmin && <button className="btn sec mini" onClick={() => confirm(`¿Borrar "${r.titulo}"?`) && guardar(lista.filter((x) => x.id !== r.id))}>Borrar</button>}
              </div>
            </div>
            <p className="tenue" style={{ margin: '.4rem 0 0', whiteSpace: 'pre-wrap', fontSize: '.88rem' }}>{rellenar(r.texto, { clinica: cl })}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
